import React, { useEffect, useRef, useState } from 'react';
import { Box, Container } from '@hover/blueprint';
import anime from 'animejs';
import HeroVideo from './hero-video';
import HeroMessageContainer from './hero-message-container';
import { HeroFormDesktop } from './hero-form';
import HeroTablet from './HeroTablet';
import { HOME_PAGE_CONTRACTOR_HOMEOWNER_MESSAGE } from '../../../constants';

const HomePageHero: React.FC = () => {
  const animationRef = useRef<ReturnType<typeof anime>>();
  const animation1Ref = useRef<ReturnType<typeof anime>>();
  const animation2Ref = useRef<ReturnType<typeof anime>>();
  const [videoDuration, setVideoDuration] = useState<null | number>(null);

  useEffect(() => {
    if (videoDuration && videoDuration > 0) {
      const { innerWidth } = window;
      const video = document.getElementById('hero-video') as HTMLVideoElement;
      const delayTime: number =
        Math.floor(
          videoDuration / HOME_PAGE_CONTRACTOR_HOMEOWNER_MESSAGE.length,
        ) * 1000;

      if (delayTime > 0) {
        animationRef.current = anime({
          autoplay: false,
          targets: '.homeowner-contractor-message',
          easing: 'linear',
          opacity: {
            value: [0, 1],
            duration: 500,
            delay(_el: any, i: number) {
              if (i === 0) {
                return 0;
              }
              if (i === 1) {
                return 3000;
              }
              if (i === 2) {
                return 6000;
              }
              if (i === 3) {
                return 10000;
              }
              if (i === 4) {
                return 14000;
              }
              return i * delayTime - 7000;
            },
          },
          translateX: {
            value: [0, '-100vw'],
            duration: delayTime - 2500,
            delay(_el: any, i: number) {
              if (i === 0) {
                return 2000;
              }
              if (i === 1) {
                return 5000;
              }
              if (i === 2) {
                return 9000;
              }
              if (i === 3) {
                return 13000;
              }
              if (i === 4) {
                return 19000;
              }
              return i * delayTime + 2000;
            },
          },
        });

        animation1Ref.current = anime({
          autoplay: false,
          targets: '.contractor-message',
          opacity: {
            value: [0, 1],
            duration: 1000,
            delay(_el: any, i: number) {
              if (i === 0) {
                return 0;
              }
              if (i === 1) {
                return 3000;
              }
              if (i === 2) {
                return 6000;
              }
              if (i === 3) {
                return 10000;
              }
              if (i === 4) {
                return 14000;
              }
              return i * delayTime;
            },
          },
          scale: {
            delay(_el: any, i: number) {
              if (i === 0) {
                return 0;
              }
              if (i === 1) {
                return 3000;
              }
              if (i === 2) {
                return 6000;
              }
              if (i === 3) {
                return 10000;
              }
              if (i === 4) {
                return 14000;
              }
              return i * delayTime;
            },
            value: [0.85, 1],
            duration: 1000,
          },
        });

        animation2Ref.current = anime({
          autoplay: false,
          targets: '.homeowner-message',
          opacity: {
            delay(_el: any, i: number) {
              if (i === 0) {
                return 0;
              }
              if (i === 1) {
                return 3000;
              }
              if (i === 2) {
                return 6000;
              }
              if (i === 3) {
                return 10000;
              }
              if (i === 4) {
                return 14000;
              }
              return i * delayTime;
            },
            value: [0, 1],
            duration: 2000,
          },
          scaleX: {
            delay(_el: any, i: number) {
              if (i === 0) {
                return 0;
              }
              if (i === 1) {
                return 3000;
              }
              if (i === 2) {
                return 6000;
              }
              if (i === 3) {
                return 10000;
              }
              if (i === 4) {
                return 14000;
              }
              return i * delayTime;
            },
            value: [0.6, 1],
            duration: 1200,
          },
        });
      }
      if (video && innerWidth > 834) {
        void video.play();
        if (
          animationRef.current &&
          animation1Ref.current &&
          animation2Ref.current
        ) {
          animationRef.current.play();
          animation1Ref.current.play();
          animation2Ref.current.play();
        }
      }
    }
  }, [videoDuration]);

  const onEnd = () => {
    if (
      animationRef.current &&
      animation1Ref.current &&
      animation2Ref.current &&
      document
    ) {
      const video = document.getElementById('hero-video') as HTMLVideoElement;

      if (video) {
        video.currentTime = 0;
        void video.play();
        animationRef.current.restart();
        animation1Ref.current.restart();
        animation2Ref.current.restart();
      }
    }
  };

  return (
    <Container maxW="100%" centerContent bg="brandTan.100" px={0}>
      <Box w="100%" maxW="8xl" bg="brandTan.100" flexDir="column">
        <HeroTablet />
        <Box
          d="flex"
          w="100%"
          justifyContent="flex-end"
          position="relative"
          pb={{ base: '190px', tablet: '113px' }}
        >
          <HeroFormDesktop />
          <HeroVideo
            onPause={() => {
              if (
                animationRef.current &&
                animation1Ref.current &&
                animation2Ref.current
              ) {
                animationRef.current.pause();
                animation1Ref.current.pause();
                animation2Ref.current.pause();
              }
            }}
            onPlay={() => {
              if (
                window &&
                animationRef.current &&
                animation1Ref.current &&
                animation2Ref.current
              ) {
                if (window.innerWidth < 834) {
                  animationRef.current.play();
                  animation1Ref.current.play();
                  animation2Ref.current.play();
                }
              }
            }}
            onEnded={onEnd}
            setVideoDuration={setVideoDuration}
          />
          {videoDuration && (
            <HeroMessageContainer videoDuration={videoDuration} />
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default HomePageHero;
